import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Phone, MapPin } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { SITE, waLink } from "../data/site";
import { useLang } from "../i18n";
import Logo from "./Logo";

export default function BookingPopup() {
  const { t } = useLang();
  const [show, setShow] = useState(false);
  const [pickup, setPickup] = useState("");
  const [drop, setDrop] = useState("");

  useEffect(() => {
    if (sessionStorage.getItem("bct_popup_seen")) return;
    const tm = setTimeout(() => {
      setShow(true);
      sessionStorage.setItem("bct_popup_seen", "1");
    }, 7000);
    return () => clearTimeout(tm);
  }, []);

  const close = () => setShow(false);

  const send = (e) => {
    e.preventDefault();
    const route =
      pickup.trim() || drop.trim()
        ? `Pickup: ${pickup || "Jodhpur"} | Drop: ${drop || "— please suggest"}`
        : "I want to book a taxi.";
    window.open(waLink(`Hello Vikash ji! ${route} Please share availability and latest fare.`), "_blank", "noopener");
    close();
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-navy/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={close}
          data-testid="booking-popup"
        >
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.97 }}
            animate={{ y: 1, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
          >
            <div className="gold-line" />
            <div className="bg-navy px-6 py-5 flex items-center justify-between grain relative">
              <div className="bg-white rounded-xl px-3 py-1.5">
                <Logo compact />
              </div>
              <button onClick={close} aria-label="Close popup" data-testid="popup-close-btn" className="text-white/60 hover:text-gold p-1.5 transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 sm:p-7">
              <h2 className="font-heading text-2xl font-extrabold text-navy tracking-tight">{t("popTitle")}</h2>
              <p className="mt-2 text-[13px] text-slate-500 leading-relaxed">{t("popSub")}</p>
              <form onSubmit={send} className="mt-5 space-y-3">
                <label className="relative block">
                  <MapPin size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-royal" />
                  <input
                    className="w-full rounded-xl border border-slate-200 px-3.5 py-3 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-royal/40 focus:border-royal"
                    placeholder={t("popPickup")}
                    value={pickup}
                    onChange={(e) => setPickup(e.target.value)}
                    data-testid="popup-pickup-input"
                  />
                </label>
                <label className="relative block">
                  <MapPin size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gold-dark" />
                  <input
                    className="w-full rounded-xl border border-slate-200 px-3.5 py-3 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-royal/40 focus:border-royal"
                    placeholder={t("popDrop")}
                    value={drop}
                    onChange={(e) => setDrop(e.target.value)}
                    data-testid="popup-drop-input"
                  />
                </label>
                <button
                  type="submit"
                  data-testid="popup-whatsapp-btn"
                  className="w-full rounded-xl bg-wa text-white font-heading font-extrabold py-3.5 text-sm tracking-wide flex items-center justify-center gap-2 hover:brightness-95 transition"
                >
                  <FaWhatsapp size={18} /> {t("popSubmit")}
                </button>
              </form>
              <div className="mt-3 flex items-center justify-between">
                <a href={`tel:${SITE.phoneTel}`} data-testid="popup-call-btn" className="inline-flex items-center gap-1.5 text-xs font-bold text-royal hover:text-gold-dark transition-colors">
                  <Phone size={13} /> {t("callNow")} {SITE.phoneDisplay}
                </a>
                <button onClick={close} data-testid="popup-later-btn" className="text-xs font-semibold text-slate-400 hover:text-slate-600 transition-colors">
                  {t("popLater")}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
